<!-- src/components/CreditPaymentPanel.vue -->
<template>
  <v-card class="credit-payment-panel" flat>
    <!-- Header -->
    <div class="cpp__top">
      <div class="cpp__title-wrap">
        </div>

      <!-- Inline create form -->
      <div class="cpp__create">
        <!-- Date: manual or calendar -->
        <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="slide-y-transition"
            offset-y
            min-width="290"
            max-width="290"
            content-class="ux-calendar"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
                v-model="dateInput"
                :disabled="creating || !contractId"
                label="Sana"
                hide-details
                outlined dense
                prepend-inner-icon="mdi-calendar"
                class="cpp__date"
                :rules="[ruleRequired, ruleDate]"
                v-bind="attrs" v-on="on"
                @input="onDateInput"
                @blur="onDateBlur"
                @keyup.enter="onDateEnter"
            />
          </template>
          <v-card class="date-picker-card" flat>
            <v-date-picker
                no-title
                :first-day-of-week="1"
                :locale="calendarLocale"
                :header-date-format="calendarHeader"
                :weekday-format="calendarWeekday"
                :month-format="calendarMonth"
                v-model="dateInput"
                color="primary"
                scrollable
                @input="dateMenu = false"
            />
          </v-card>
        </v-menu>

        <v-text-field
            v-model="amountInput"
            :disabled="creating || !contractId"
            label="To‘langan summa"
            outlined dense
            hide-details
            prepend-inner-icon="mdi-cash-check"
            class="cpp__amount"
            :rules="[ruleNonNegative]"
            @focus="moneyFocus=true"
            @blur="moneyFocus=false; formatInput()"
            @keyup.enter="submit"
        />
        <v-btn
            :loading="creating"
            class="cpp__btn-create"
            @click="submit"
        >
          <v-icon left small style="margin-right:6px">mdi-check</v-icon>
          Qo‘shish
        </v-btn>
      </div>
    </div>

    <!-- Summary -->
    <div class="cpp__summary">
      <div class="cpp__sum-card">
        <div class="cpp__sum-icon">
          <v-icon>mdi-cash-multiple</v-icon>
        </div>
        <div>
          <div class="cpp__sum-label">Umumiy to‘langan</div>
          <div class="cpp__sum-value">{{ money(totalPaid) }}</div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="cpp__loading">
      <v-skeleton-loader type="table"/>
    </div>

    <!-- Table -->
    <v-data-table
        v-else
        :headers="headers"
        :items="items"
        class="cpp__table"
        hide-default-footer
        item-key="id"
        dense
    >
      <template v-slot:item.index="{ index }">
        <div class="cpp__index">{{ index + 1 }}</div>
      </template>

      <template v-slot:item.paymentDate="{ item }">
        <v-chip
            outlined small label class="cpp__chip no-row-click"
            @click="openEdit(item)" title="Tahrirlash"
        >
          <v-icon left small>mdi-calendar</v-icon>
          {{ fmtDate(item.paymentDate) }}
        </v-chip>
      </template>

      <template v-slot:item.paid="{ item }">
        <v-chip
            outlined small label class="cpp__chip no-row-click"
            @click="openEdit(item)" title="Tahrirlash"
        >
          <v-icon left small>mdi-cash-check</v-icon>
          {{ money(item.paid) }}
        </v-chip>
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="cpp__actions no-row-click">
          <v-btn icon small class="mr-1" @click="openEdit(item)" :title="'Tahrirlash'">
            <v-icon small>mdi-pencil-outline</v-icon>
          </v-btn>
          <v-btn icon small color="error" @click="removeItem(item)" :title="'O‘chirish'">
            <v-icon small>mdi-trash-can-outline</v-icon>
          </v-btn>
        </div>
      </template>

      <template v-slot:no-data>
        <div class="cpp__empty">
          <v-icon size="48" color="grey lighten-1">mdi-clipboard-text-outline</v-icon>
          <h3>Ma’lumot yo‘q</h3>
        </div>
      </template>
    </v-data-table>

    <!-- Edit dialog -->
    <v-dialog v-model="edit.open" max-width="520px" persistent>
      <v-card class="dlg">
        <div
            class="dlg__head"
            :style="{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #edf2f7',
            background: '#fafafa',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px'
          }"
        >
          <div class="dlg__title-wrap" :style="{ display: 'flex', alignItems: 'center', minWidth: 0 }">
          <div
              class="dlg__icon"
              :style="{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px'
              }"
          >
            <v-icon small color="#374151">{{ 'mdi-pencil' }}</v-icon>
          </div>

          <div class="dlg__texts" :style="{ display: 'flex', flexDirection: 'column', minWidth: 0 }">
            <h2
                class="dlg__title"
                :style="{
                  fontSize: '18px',
                  lineHeight: 1.2,
                  fontWeight: 600,
                  color: '#111827',
                  margin: 0,
                  padding: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }"
            >
              To'lovni tahrirlash
            </h2>

            <div
                class="dlg__subtitle"
                :style="{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '4px',
                  fontSize: '12px',
                  color: '#6b7280'
                }"
            >
              <v-icon x-small class="mr-1" color="#6b7280" style="margin-right:6px">mdi-file-document</v-icon>
              <span :style="{ overflow: 'hidden', textOverflow: 'ellipsis' }">
                  {{ contractNumber || ('#' + contractId) }}
                </span>
            </div>
          </div>
        </div>
        </div>

        <v-divider/>
        <v-card-text class="dlg__body mt-4">
          <!-- Date -->
          <v-menu
              v-model="edit.dateMenu"
              :close-on-content-click="false"
              transition="slide-y-transition"
              offset-y
              min-width="290"
              max-width="290"
              content-class="ux-calendar"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                  v-model="edit.dateInput"
                  label="Sana"
                  outlined dense
                  prepend-inner-icon="mdi-calendar"
                  :rules="[ruleRequired, ruleDate]"
                  v-bind="attrs" v-on="on"
                  @input="onEditDateInput"
                  @blur="onEditDateBlur"
                  @keyup.enter="onEditDateEnter"
              />
            </template>
            <v-card class="date-picker-card" flat>
              <v-date-picker
                  no-title
                  :first-day-of-week="1"
                  :locale="calendarLocale"
                  :header-date-format="calendarHeader"
                  :weekday-format="calendarWeekday"
                  :month-format="calendarMonth"
                  v-model="edit.dateInput"
                  color="primary"
                  scrollable
                  @input="edit.dateMenu=false"
              />
            </v-card>
          </v-menu>

          <!-- Amount -->
          <v-text-field
              v-model="edit.amountInput"
              label="To‘langan summa"
              outlined dense
              prepend-inner-icon="mdi-cash-check"
              :rules="[ruleNonNegative]"
              @focus="edit.moneyFocus=true"
              @blur="edit.moneyFocus=false; formatEditInput()"
              @keyup.enter="saveEdit"
          />
        </v-card-text>
        <v-card-actions class="dlg__actions">
          <v-spacer/>
          <v-btn :style="{
                border: '1px solid #e5e7eb',
                color: '#00274c',
                background: '#f9fafb',
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: '8px',
                padding: '6px 16px'
              }" outlined @click="edit.open=false">
            <v-icon left small>mdi-close</v-icon>
            Bekor qilish
          </v-btn>
          <v-btn depressed color="primary"
                 :style="{
                background: '#00274c',
                color: '#fff',
                borderRadius: '8px',
                fontWeight: 600,
                textTransform: 'none',
                padding: '6px 18px'
              }" :loading="edit.saving" @click="saveEdit">
            <v-icon left small>mdi-check</v-icon>
            Saqlash
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        top right
        timeout="2600"
        class="cpp__snack"
    >
      <div class="cpp__snack-content">
        <v-icon v-if="snackbar.color !== 'error'" class="mr-2">mdi-check-circle</v-icon>
        <v-icon v-else class="mr-2" color="white">mdi-alert-circle</v-icon>
        {{ snackbar.text }}
      </div>
    </v-snackbar>

    <!-- Guard if contractId missing -->
    <v-alert v-if="!contractId" type="warning" outlined class="ma-4">
      Kontrakt ID topshirilgani yo‘q. Componentga <code>:contract-id</code> bering.
    </v-alert>

    <app-confirm
      v-model="deleteConfirm"
      :text="$t('Are you sure you want to delete?')"
      :confirm-text="$t('Delete')"
      @accept="confirmRemove"
    />
  </v-card>
</template>

<script>
import axios from 'axios';
import calendar from '@/mixins/calendar';
import AppConfirm from '@/components/modals/app-confirm.vue';

export default {
  name: 'CreditPaymentPanel',
  components: { AppConfirm },
  mixins: [calendar],
  props: {
    contractId: {type: Number, required: true},
    historyId: {type: Number, required: true},
    apiBase: {type: String, default: '/credit/payment'},
    dir: {type: String, default: 'desc'}, // or 'asc'
    formatLocale: {type: String, default: 'en-US'}, // "100,000" style
    reloadHistory: {
      type: Function,
      default: () => {
        console.warn('No reload function provided');
      },
      required: true
    },
  },
  data: () => ({
    deleteConfirm: false,
    deleteTarget: null,
    headers: [
      {text: '№', value: 'index', sortable: false, width: 70},
      {text: 'Sana', value: 'paymentDate'},
      {text: "To‘langan", value: 'paid'},
      {text: 'Amallar', value: 'actions', sortable: false, width: 110}
    ],
    items: [],
    loading: false,
    creating: false,

    // create inputs
    amountInput: '',
    moneyFocus: false,
    dateMenu: false,
    dateInput: '',

    // edit dialog state
    edit: {
      open: false,
      saving: false,
      item: null,
      dateInput: '',
      amountInput: '',
      dateMenu: false,
      moneyFocus: false
    },

    snackbar: {show: false, text: '', color: 'success'}
  }),
  computed: {
    canSubmit() {
      const n = this.parseMoney(this.amountInput);
      return this.contractId && !isNaN(n) && n > 0 && !!this.normalizeDate(this.dateInput);
    },
    totalPaid() {
      return this.items.reduce((a, b) => a + Number(b.paid || 0), 0);
    }
  },
  filters: {
    nf(v) {
      return new Intl.NumberFormat('uz-UZ').format(Number(v || 0));
    }
  },
  watch: {
    contractId: {
      immediate: true,
      handler(v) {
        if (v) {
          this.dateInput = this.todayIso();
          this.reload();
        }
      }
    }
  },
  methods: {
    // ====== API ======
    async reload() {
      if (!this.contractId || this.loading) return;
      this.loading = true;
      try {
        const url = `${this.apiBase}/all/by/${this.historyId}`;
        const {data} = await axios.get(url);
        const list = Array.isArray(data) ? data : (data?.items || data?.content || []);
        const normalized = list.map(x => ({
          id: x.id,
          paid: Number(x.paid ?? 0),
          paymentDate: x.paymentDate || x.date || (x.createdAt ? x.createdAt.substr(0, 10) : null),
          contractId: x.contractId,
          creditHistoryId: x.historyId,
        }));

        // client-side sort safety
        this.items = normalized.sort((a, b) => {
          const ta = a.paymentDate ? new Date(a.paymentDate + 'T00:00:00').getTime() : 0;
          const tb = b.paymentDate ? new Date(b.paymentDate + 'T00:00:00').getTime() : 0;
          return this.dir.toLowerCase() === 'asc' ? ta - tb : tb - ta;
        });
      } catch (e) {
        console.error('Failed to load payments', e);
        this.toast('To‘lovlar ro‘yxatini yuklashda xatolik.', 'error');
      } finally {
        this.loading = false;
      }
    },

    async submit() {
      if (!this.canSubmit) return;
      const paid = this.parseMoney(this.amountInput);
      const paymentDate = this.normalizeDate(this.dateInput) || this.todayIso(); // 'YYYY-MM-DD'

      this.creating = true;
      try {
        await axios.post(`${this.apiBase}/create`, {
          contractId: this.contractId,
          paid,
          paymentDate,
          creditHistoryId: this.historyId,
        });
        this.toast('To‘lov qo‘shildi.');
        this.amountInput = '';
        this.dateInput = this.todayIso();
        this.reloadHistory();
        this.reload();
      } catch (e) {
        console.error('Create payment failed', e);
        this.toast('To‘lovni saqlashda xatolik.', 'error');
      } finally {
        this.creating = false;
      }
    },

    async saveEdit() {
      if (!this.edit.item) return;
      const paid = this.parseMoney(this.edit.amountInput);
      const paymentDate = this.normalizeDate(this.edit.dateInput);

      if (!paymentDate) {
        this.toast("Sana noto‘g‘ri. 'YYYY-MM-DD' formatida kiriting.", 'error');
        return;
      }
      if (isNaN(paid) || paid < 0) {
        this.toast("Summani to‘g‘ri kiriting.", 'error');
        return;
      }

      this.edit.saving = true;
      try {
        await axios.put(`${this.apiBase}/${this.edit.item.id}`, {
          contractId: this.edit.item.contractId || this.contractId,
          paid,
          paymentDate
        });
        this.toast('To‘lov yangilandi.');
        this.edit.open = false;
        this.reload();
      } catch (e) {
        console.error('Update failed', e);
        this.toast('Tahrirlashda xatolik.', 'error');
      } finally {
        this.edit.saving = false;
        this.reloadHistory()
      }
    },

    removeItem(item) {
      if (!item || !item.id) return;
      this.deleteTarget = item;
      this.deleteConfirm = true;
    },

    async confirmRemove() {
      const item = this.deleteTarget;
      this.deleteTarget = null;
      if (!item) return;
      try {
        await axios.delete(`${this.apiBase}/${item.id}`);
        this.toast("To‘lov o‘chirildi.");
        this.reload();
        this.reloadHistory();
      } catch (e) {
        console.error('Delete failed', e);
        this.toast("O‘chirishda xatolik.", 'error');
      }
    },

    // ====== Edit helpers ======
    openEdit(item) {
      this.edit.item = {...item};
      this.edit.dateInput = item.paymentDate || this.todayIso();
      this.edit.amountInput = this.formatMoney(item.paid || 0);
      this.edit.open = true;
    },
    onEditDateInput() { /* no-op */
    },
    onEditDateBlur() {
      const norm = this.normalizeDate(this.edit.dateInput);
      if (norm) this.edit.dateInput = norm;
    },
    onEditDateEnter() {
      this.onEditDateBlur();
      this.edit.dateMenu = false;
    },
    formatEditInput() {
      const n = this.parseMoney(this.edit.amountInput);
      this.edit.amountInput = this.formatMoney(n);
    },

    // ====== Date helpers ======
    todayIso() {
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },
    onDateInput() { /* no-op */
    },
    onDateBlur() {
      const norm = this.normalizeDate(this.dateInput);
      if (norm) this.dateInput = norm;
    },
    onDateEnter() {
      this.onDateBlur();
      this.dateMenu = false;
    },
    normalizeDate(s) {
      if (!s) return null;
      const t = String(s).trim().replace(/[.\s/]/g, '-').replace(/--+/g, '-');
      if (/^\d{4}-\d{2}-\d{2}$/.test(t) && this.isValidYMD(t)) return t;
      if (/^\d{2}-\d{2}-\d{4}$/.test(t)) {
        const [dd, mm, yyyy] = t.split('-').map(n => parseInt(n, 10));
        if (this.isValidParts(yyyy, mm, dd)) {
          return `${String(yyyy).padStart(4, '0')}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
        }
        return null;
      }
      if (/^\d{8}$/.test(t)) {
        // try YYYYMMDD or DDMMYYYY
        const a = t.slice(0, 4), b = t.slice(4, 6), c = t.slice(6, 8);
        const first = parseInt(a, 10);
        if (first >= 1900 && first <= 2100) {
          const yyyy = first, mm = parseInt(b, 10), dd = parseInt(c, 10);
          if (this.isValidParts(yyyy, mm, dd)) return `${yyyy}-${b}-${c}`;
        } else {
          const dd = parseInt(t.slice(0, 2), 10);
          const mm = parseInt(t.slice(2, 4), 10);
          const yyyy = parseInt(t.slice(4, 8), 10);
          if (this.isValidParts(yyyy, mm, dd)) {
            return `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
          }
        }
        return null;
      }
      return null;
    },
    isValidYMD(ymd) {
      const [y, m, d] = ymd.split('-').map(n => parseInt(n, 10));
      return this.isValidParts(y, m, d);
    },
    isValidParts(y, m, d) {
      if (!(y >= 1900 && y <= 2100)) return false;
      if (!(m >= 1 && m <= 12)) return false;
      const dim = new Date(y, m, 0).getDate();
      return d >= 1 && d <= dim;
    },
    ruleRequired(v) {
      return !!v || 'Majburiy';
    },
    ruleDate(v) {
      return !v || this.normalizeDate(v) ? true : "Sana 'YYYY-MM-DD' formatida";
    },

    // ====== Money helpers (keep 100,000 as 100,000) ======
    parseMoney(v) {
      if (v === '' || v == null) return 0;
      const s = String(v).trim()
          .replace(/\s/g, '')
          .replace(/[.,\u00A0]/g, '')      // remove thousands separators
          .replace(/(?!^-)[^0-9]/g, '');   // keep only digits, allow leading minus
      const num = Number(s);
      return isNaN(num) ? 0 : num;
    },
    formatMoney(n) {
      return new Intl.NumberFormat(this.formatLocale).format(Number(n || 0));
    },
    formatInput() {
      const n = this.parseMoney(this.amountInput);
      this.amountInput = this.formatMoney(n);
    },
    money(v) {
      return this.formatMoney(v);
    },
    fmtDate(d) {
      if (!d) return '';
      const dt = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T00:00:00') : new Date(d);
      return dt.toLocaleString('uz-UZ', {year: 'numeric', month: '2-digit', day: '2-digit'});
    },
    ruleNonNegative(v) {
      const n = this.parseMoney(v);
      return (v === '' || v == null || (!isNaN(n) && n >= 0)) || "0 dan kichik bo‘lmasin";
    },
    toast(text, type = 'success') {
      this.snackbar = {show: true, text, color: type === 'error' ? 'error' : 'success'};
    }
  }
};
</script>

<style lang="scss" scoped>
$bg: #fff;
$ink: #00274c;
$muted: #555;
$line: #e6e6e6;
$soft: #f6f6f6;
$radius: 12px;
$shadow: 0 6px 16px rgba(0, 0, 0, .06);

.credit-payment-panel {
  background: $bg;
  border-radius: $radius !important;
  box-shadow: $shadow;

  .cpp__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    padding: 16px 20px;
  }

  .cpp__title-wrap {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .cpp__title {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
    color: $ink;
  }

  .cpp__subtitle {
    font-size: 12px;
    color: $muted;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .cpp__create {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }

  .cpp__date {
    min-width: 160px;
  }

  .cpp__amount {
    min-width: 220px;
  }

  .cpp__btn-create {
    background: #00274c !important;
    color: #fff !important;
    border-radius: 10px;
    text-transform: none;
    font-weight: 700;
  }

  .cpp__summary {
    display: grid;
    gap: 14px;
    padding: 16px 20px;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }

  .cpp__sum-card {
    border: 1px solid $line;
    border-radius: $radius;
    padding: 14px;
    display: flex;
    align-items: center;
    gap: 12px;
    background: $bg;
  }

  .cpp__sum-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: grid;
    place-items: center;
    background: $soft;
    color: $ink;
  }

  .cpp__sum-label {
    font-size: 12px;
    color: $muted;
    font-weight: 600;
  }

  .cpp__sum-value {
    font-size: 18px;
    font-weight: 800;
    color: $ink;
    font-variant-numeric: tabular-nums;
  }

  .cpp__loading {
    padding: 16px 20px;
  }

  .cpp__table {
    background: transparent;
    margin: 0 20px 20px;

    ::v-deep .v-data-table__wrapper {
      background: $bg;
      border: 1px solid $line;
      border-radius: $radius;
    }

    ::v-deep thead {
      background: $soft;

      th {
        background: transparent !important;
        color: $muted !important;
        font-weight: 800 !important;
        font-size: 11px !important;
        text-transform: uppercase;
        letter-spacing: .05em;
        border-bottom: 1px solid $line !important;
        padding: 12px !important;
      }
    }

    ::v-deep td {
      padding: 12px !important;
      border-bottom: 1px solid $line !important;
    }

    .cpp__index {
      font-weight: 700;
      color: $muted;
    }

    .cpp__chip {
      border-color: #bbb !important;
      font-weight: 600;
      cursor: pointer;
    }

    .cpp__actions .v-btn {
      border-radius: 8px;
    }

    .cpp__empty {
      text-align: center;
      padding: 40px 16px;
      color: $muted;

      h3 {
        margin-top: 8px;
        color: $ink;
      }
    }
  }

  .date-picker-card {
    border: 1px solid $line !important;
    border-radius: $radius !important;
    box-shadow: $shadow !important;
  }

  .dlg {
    background: $bg !important;
    border: 1px solid $line !important;
    border-radius: $radius !important;
    box-shadow: $shadow !important;

    .dlg__head {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      border-bottom: 1px solid $line;
    }

    .dlg__title {
      margin: 0;
      font-weight: 800;
    }

    .dlg__body {
      padding: 16px;
    }

    .dlg__actions {
      padding: 12px 16px;
      border-top: 1px solid $line;
    }
  }

  .cpp__snack {
    ::v-deep .v-snack__wrapper {
      border-radius: 10px !important;
      box-shadow: $shadow !important;
      background: $ink;
      color: #fff;
    }

    .cpp__snack-content {
      display: flex;
      align-items: center;
      gap: 10px;
      font-weight: 600;
    }
  }

  @media (max-width: 768px) {
    .cpp__amount {
      min-width: 180px;
    }
  }
}

.no-row-click {
  pointer-events: auto;
}

.title {
  font-size: 22px;
  font-weight: 800;
  color: $ink;
  margin: 0 0 2px;
}
@media (max-width: 600px) {
  .credit-payment-panel .cpp__create { flex-wrap: wrap; }
  /* the date field's real flex item is its wrapping .v-menu, so stretch that too */
  .credit-payment-panel .cpp__create > .v-menu { flex: 1 1 100%; min-width: 100%; }
  .credit-payment-panel .cpp__date, .credit-payment-panel .cpp__amount { min-width: 100%; flex: 1 1 100%; }
}
</style>
